/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import uuid from 'uuid';
import classnames from 'classnames';
import IconMapping from '../../../../utils/iconMapping';
import { PDPAnalytics } from '../../../../analytics';
import { APP_PLATFORM } from '../../../../utils/modelMapper/appModelMapper/appModelConstants';
import { PDP_SECTION, INFORMATION_FIELD_TYPES } from '../../../../utils/pdpUtils/pdpEnums';
import TranslationUtil from '../../../../utils/translation';
import { getOSDisplayString } from '../../../../utils/commonUtil';
import Config from '../../../../stores/config';

const DEFAULT_PRIMARY_LANGUAGE = 'English';

const getLanguageList = (supportedLanguages, locale) => {
    const languageList = supportedLanguages.map(language => language.languageName);
    if (!locale) {
        return languageList;
    }
    const currentLocaleLanguageList = supportedLanguages
        .filter(language => language.languageId === locale);
    const primaryLanguage = currentLocaleLanguageList.length
        ? currentLocaleLanguageList[0].languageName : DEFAULT_PRIMARY_LANGUAGE;
    if (languageList.indexOf(primaryLanguage) > 0) {
        const primaryLanguageInd = languageList.indexOf(primaryLanguage);
        languageList.splice(primaryLanguageInd, 1);
        languageList.unshift(primaryLanguage);
    }
    return languageList;
};

const getCompatibleOSStringObj = (supportedOSList) => {
    let localizedText; let enText;
    // Filter empty entries(if any)
    const filteredSupportedOS = supportedOSList.filter(Boolean);
    if (filteredSupportedOS.length === 1) {
        localizedText = getOSDisplayString(filteredSupportedOS[0]);
        enText = getOSDisplayString(filteredSupportedOS[0]);
    } else if (filteredSupportedOS.length === 2) {
        localizedText = TranslationUtil.getMessage('ccx.pdp.information.compatibility.supportedOS.two', {
            firstOS: getOSDisplayString(filteredSupportedOS[0]),
            secondOS: getOSDisplayString(filteredSupportedOS[1]),
        });
        enText = `${getOSDisplayString(supportedOSList[0])} and ${getOSDisplayString(supportedOSList[1])}`;
    } else if (filteredSupportedOS.length === 3) {
        localizedText = TranslationUtil.getMessage('ccx.pdp.information.compatibility.supportedOS.three', {
            firstOS: getOSDisplayString(filteredSupportedOS[0]),
            secondOS: getOSDisplayString(filteredSupportedOS[1]),
            thirdOS: getOSDisplayString(filteredSupportedOS[2]),
        });
        enText = `${getOSDisplayString(filteredSupportedOS[0])}, ${getOSDisplayString(supportedOSList[1])} and ${getOSDisplayString(supportedOSList[2])}`;
    } else if (supportedOSList.length === 4) {
        localizedText = TranslationUtil.getMessage('ccx.pdp.information.compatibility.supportedOS.four', {
            firstOS: getOSDisplayString(filteredSupportedOS[0]),
            secondOS: getOSDisplayString(filteredSupportedOS[1]),
            thirdOS: getOSDisplayString(filteredSupportedOS[2]),
            fourthOS: getOSDisplayString(filteredSupportedOS[3]),
        });
        enText = `${getOSDisplayString(filteredSupportedOS[0])}, ${getOSDisplayString(supportedOSList[1])}, ${getOSDisplayString(supportedOSList[2])} and ${getOSDisplayString(supportedOSList[3])}`;
    }
    return { localizedText, enText };
};

class Information extends Component {
    onInfoLabelClick(e, infoObj) {
        const {
            PDPDataObject: {
                actionHandlerCallback,
            },
        } = this.props;
        const { sectionId, appObject } = this.sectionData;
        const guid = uuid.v4();
        PDPAnalytics.informationClick(
            infoObj,
            guid,
            sectionId,
            this.getFieldValueText(infoObj).enText
        );
        const actionParam = {
            actionObject: infoObj,
            appObject,
            guid,
        };
        actionHandlerCallback(e, sectionId, infoObj.actionId, actionParam);
    }

    get sectionData() {
        const { PDPDataObject, appsList } = this.props;
        return this.validateAndProcessData(PDPDataObject, appsList);
    }

    getCompatibilityString(fieldValue, supportedOSList) {
        if (fieldValue === undefined) {
            if (supportedOSList && supportedOSList.length) {
                return getCompatibleOSStringObj(supportedOSList);
            }
            return {
                localizedText: TranslationUtil.getMessage('ccx.pdp.information.compatibility.system-requirements'),
                enText: 'System Requirements',
            };
        }
        if (fieldValue === true) {
            return {
                localizedText: TranslationUtil.getMessage('ccx.pdp.information.compatibility.compatible'),
                enText: 'Compatible',
            };
        }
        return {
            localizedText: TranslationUtil.getMessage('ccx.pdp.information.compatibility.in-compatible'),
            enText: 'Not compatible',
        };
    }

    getFieldValueText(infoObj) {
        switch (infoObj.actionId) {
        case INFORMATION_FIELD_TYPES.COMPATIBILITY:
            const compatibilityTextObj = this.getCompatibilityString(
                infoObj.fieldValue,
                infoObj.supportedOSList
            );
            return {
                localizedText: compatibilityTextObj.localizedText,
                enText: compatibilityTextObj.enText,
            };
        case INFORMATION_FIELD_TYPES.LANGUAGES:
            return infoObj.fieldValue.length > 1
                ? {
                    localizedText: TranslationUtil.getMessage('ccx.pdp.information.languages.value', {
                        primaryLang: infoObj.fieldValue[0],
                        numLang: infoObj.fieldValue.length - 1,
                    }),
                    enText: `${infoObj.fieldValue[0]} and ${infoObj.fieldValue.length - 1} more`,
                }
                : {
                    localizedText: infoObj.fieldValue[0],
                    enText: infoObj.fieldValue[0],
                };
        default:
            return {
                localizedText: infoObj.fieldValue,
                enText: infoObj.fieldValue,
            };
        }
    }

    validateAndProcessData({
        contentfulData, app, locale,
    }, appsList) {
	    const informationFieldList = [];
	    let compatibilityUrlData = {
	        url: TranslationUtil.getMessage('ccx.url.systemRequirements'),
	        targetClientID: 'CreativeCloudWeb1',
	        targetScope: 'openid,creative_cloud',
	    };
	    if (contentfulData.compatibilityUrl && contentfulData.compatibilityUrl.url) {
	        compatibilityUrlData = contentfulData.compatibilityUrl;
	    }
	    // Will Not be displayed Web Section
	    if (app.platform !== APP_PLATFORM.WEB && app.showCompatibility !== false) {
	        const compatibilityIcon = !app.isIncompatible ? 'CheckmarkCircle' : 'Alert';
	        const compatibilityInfo = {
	            actionId: INFORMATION_FIELD_TYPES.COMPATIBILITY,
                fieldValue: (app.platform === APP_PLATFORM.DESKTOP
                    && !Config.isNonDownloadableSurface)
				    ? !app.isIncompatible : undefined,
	            supportedOSList: (app.platform === APP_PLATFORM.MOBILE)
				    ? app.supportedOS : null,
	            fieldUrl: compatibilityUrlData.url,
	            urlData: compatibilityUrlData,
                icon: (app.platform === APP_PLATFORM.DESKTOP
                    && !Config.isNonDownloadableSurface)
				    ? compatibilityIcon : null,
	        };
	        informationFieldList.push(compatibilityInfo);
	    }

	    if (contentfulData.supportedLanguages && contentfulData.supportedLanguages.length) {
	        const languageList = getLanguageList(contentfulData.supportedLanguages, locale);
	        informationFieldList.push({
	            actionId: INFORMATION_FIELD_TYPES.LANGUAGES,
	            fieldValue: languageList,
		    });
	    }

	    if (app && app.latestVersion && app.platform === APP_PLATFORM.DESKTOP) {
	        const appsInSessionWithSapcode = appsList
			    .filter(appObj => (appObj.sapcode === app.sapcode)
							    && appObj.progress
						        && (appObj.progress.inProgress || appObj.progress.isWaiting));
	        const versionInfo = {
	            actionId: INFORMATION_FIELD_TYPES.VERSION,
	            fieldValue: app.latestVersion,
	            disabled: appsInSessionWithSapcode.length,
	        };
	        informationFieldList.push(versionInfo);
	    }

	    if (!informationFieldList.length) {
	        return null;
	    }

	    return {
	        sectionId: PDP_SECTION.INFORMATION,
	        appObject: app,
            informationFieldList,
	    };
    }

    renderFieldValueIcon(infoObj) {
        const { ChevronRight, LinkOut } = IconMapping;
        if (infoObj.fieldUrl) {
            return <LinkOut size="XS" className="information-field-linkout" />;
        }
        return <ChevronRight size="XS" className="information-field-chevron" />;
    }

    renderInformationFieldsLargeScreen() {
        const { informationFieldList } = this.sectionData;
        const { LinkOut } = IconMapping;
        return informationFieldList.map((infoObj) => {
            const Icon = infoObj.icon ? IconMapping[infoObj.icon] : null;
            const disableLink = infoObj.disabled
                || (infoObj.actionId === INFORMATION_FIELD_TYPES.COMPATIBILITY
                        && !infoObj.fieldUrl);
            return (
                <div className="information-field-large-screen" key={infoObj.actionId}>
                    <div className="information-field-name">
                        {`${TranslationUtil.getMessage(`ccx.pdp.information.${infoObj.actionId}`)}`}
                    </div>
                    <a
                        tabIndex="0"
                        role="button"
                        aria-label={`${infoObj.actionId}, ${this.getFieldValueText(infoObj).localizedText}`}
                        className={classnames('information-field-value', { disabled: disableLink })}
                        onClick={(e) => { this.onInfoLabelClick(e, infoObj); }}
                        onKeyDown={(e) => {
                            if (e.keyCode === 13) {
                                this.onInfoLabelClick(e, infoObj);
                            }
                        }}>
                        {Icon && (infoObj.icon === 'CheckmarkCircle'
                            ? (<Icon size="XS" className="information-field-value-icon information-field-value-icon-compatible" />)
                            : (<Icon size="XS" className="information-field-value-icon" />))}
                        <span className="information-field-value-text">
                            {this.getFieldValueText(infoObj).localizedText}
                        </span>
                        {infoObj.fieldUrl && <LinkOut size="XS" className="information-field-linkout" />}
                    </a>
                </div>
            );
        });
    }

    renderInformationFieldsSmallScreen() {
        const { informationFieldList } = this.sectionData;
        return informationFieldList.map((infoObj) => {
            if (infoObj.actionId === INFORMATION_FIELD_TYPES.COMPATIBILITY && !infoObj.fieldUrl) {
                return null;
            }
            return (
                <a
                    tabIndex="0"
                    role="button"
                    aria-label={infoObj.actionId}
                    className="information-field-small-screen"
                    key={infoObj.actionId}
                    onClick={(e) => { this.onInfoLabelClick(e, infoObj); }}
                    onKeyDown={(e) => {
                        if (e.keyCode === 13) {
                            this.onInfoLabelClick(e, infoObj);
                        }
                    }}>
                    <span> {`${TranslationUtil.getMessage(`ccx.pdp.information.${infoObj.actionId}`)}`} </span>
                    {this.renderFieldValueIcon(infoObj)}
                </a>
            );
        });
    }

    render() {
        // Information
        return this.sectionData && (
            <div className="information right-panel-compt">
                <div className="information-header">
                    {TranslationUtil.getMessage('ccx.pdp.information.section-name')}
                </div>
                <div className="information-fields">
                    {this.renderInformationFieldsLargeScreen()}
                    {this.renderInformationFieldsSmallScreen()}
                </div>
            </div>
        );
    }
}

export default observer(Information);
